/**
 * Solves the Lights Out puzzle using Gaussian elimination over GF(2).
 *
 * Given the current board state, returns the list of (x, y) coordinates
 * that need to be pressed to turn all lights on, or null if unsolvable.
 */
export function solveLightsOut(
  gridSize: number,
  litStates: boolean[][]
): [number, number][] | null {
  const n = gridSize * gridSize;

  // Build the toggle matrix A where A[i][j] = 1 means pressing cell j
  // toggles cell i. Augment with the target column.
  // Target: we need to toggle every cell that is currently OFF.
  const matrix: number[][] = [];
  for (let i = 0; i < n; i++) {
    const row = new Array(n + 1).fill(0);
    const iy = Math.floor(i / gridSize);
    const ix = i % gridSize;

    // Pressing cell j toggles cell i if j == i or j is adjacent to i
    for (let j = 0; j < n; j++) {
      const jy = Math.floor(j / gridSize);
      const jx = j % gridSize;
      if (
        (ix === jx && iy === jy) ||
        (ix === jx && Math.abs(iy - jy) === 1) ||
        (iy === jy && Math.abs(ix - jx) === 1)
      ) {
        row[j] = 1;
      }
    }

    // Target: 1 if the light is OFF (needs to be toggled on)
    row[n] = litStates[iy][ix] ? 0 : 1;
    matrix.push(row);
  }

  // Gaussian elimination over GF(2)
  const pivotCol: number[] = [];
  let row = 0;
  for (let col = 0; col < n && row < n; col++) {
    // Find pivot
    let pivotRow = -1;
    for (let r = row; r < n; r++) {
      if (matrix[r][col] === 1) {
        pivotRow = r;
        break;
      }
    }
    if (pivotRow === -1) continue;

    // Swap rows
    [matrix[row], matrix[pivotRow]] = [matrix[pivotRow], matrix[row]];
    pivotCol.push(col);

    // Eliminate
    for (let r = 0; r < n; r++) {
      if (r !== row && matrix[r][col] === 1) {
        for (let c = 0; c <= n; c++) {
          matrix[r][c] ^= matrix[row][c];
        }
      }
    }
    row++;
  }

  // Check for inconsistency (row with all zeros on left but 1 on right)
  for (let r = row; r < n; r++) {
    if (matrix[r][n] === 1) return null;
  }

  // Read solution — pressed cells have value 1
  const solution = new Array(n).fill(0);
  for (let i = 0; i < pivotCol.length; i++) {
    solution[pivotCol[i]] = matrix[i][n];
  }

  const moves: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    if (solution[i] === 1) {
      const x = i % gridSize;
      const y = Math.floor(i / gridSize);
      moves.push([x, y]);
    }
  }

  return moves;
}
